import { BrandSearchResult, ExhibitorSearchResult } from '@planone/bau365/data-access-fair-data';
import { SearchResult } from '@planone/bau365/data-access-search';

import { DataTransformationSnowplowMapperInterface } from './data-transformation-mapper.interface';

import { WatchlistChangeInterface } from '../state/watchlist/watchlist.models';
import { CustomEventIglu } from '../tracking-types/snowplow/schema/schema.interface';

export class DataTransformationSnowplowMapper implements DataTransformationSnowplowMapperInterface {
  private transformDate(date: any) {
    if (date instanceof Date) {
      return date.toISOString();
    }

    return date;
  }

  constructor(private locale: string) {}

  map(
    entityType: 'EVENT' | 'EXHIBITOR' | 'POST' | 'PRODUCT' | 'CONTACT_PERSON' | 'CONTRIBUTOR',
    entity: any,
    context: string | undefined = undefined
  ) {
    switch (entityType) {
      case 'EVENT':
        return {
          schema: CustomEventIglu.entity_event,
          data: {
            context: <string>entity.fair_id,
            locale: this.locale,
            event_id: entity.id,
            event_name: entity.name ?? entity.headline,
            ...(entity.starts_at && { starts_at: this.transformDate(entity.starts_at) }),
            ...(entity.ends_at && { ends_at: this.transformDate(entity.ends_at) }),
          },
        };
      case 'EXHIBITOR':
        return {
          schema: CustomEventIglu.entity_exhibitor,
          data: {
            context: <string>entity.fair_id,
            locale: this.locale,
            exhibitor_id: <string>entity.id,
            exhibitor_name: entity.name,
          },
        };
      case 'POST':
        return {
          schema: CustomEventIglu.entity_post,
          data: {
            context: entity.fair_id,
            locale: this.locale,
            post_id: <string>entity.id,
            post_name: entity.name ?? entity.headline,
            ...(entity.published_at && { published_at: this.transformDate(entity.published_at) }),
          },
        };
      case 'PRODUCT':
        return {
          schema: CustomEventIglu.entity_product,
          data: {
            context: context,
            locale: this.locale,
            product_id: <string>entity.id,
            product_name: entity.name,
            ...(entity.published_at && { published_at: this.transformDate(entity.published_at) }),
          },
        };
      case 'CONTACT_PERSON':
        return {
          schema: CustomEventIglu.entity_contact_person,
          data: {
            context: context,
            locale: this.locale,
            contact_id: <string>entity.id,
          },
        };
      case 'CONTRIBUTOR':
        return {
          schema: CustomEventIglu.entity_contact_person,
          data: {
            context: context,
            locale: this.locale,
            contact_id: <string>entity.id,
          },
        };
      default:
        throw new Error('Method not implemented.');
    }
  }

  mapArray(
    entityType: 'SEARCH_RESULT' | 'EXHIBITOR_SEARCH_RESULT' | 'BRAND_SEARCH_RESULT' | 'WATCHLIST',
    entity: any,
    context?: string
  ) {
    switch (entityType) {
      case 'SEARCH_RESULT':
        return this.mapSearchResult(entity, context);
      case 'EXHIBITOR_SEARCH_RESULT':
      case 'BRAND_SEARCH_RESULT':
        return this.mapExtendedSearchResult(entity, context);
      case 'WATCHLIST':
        return this.mapWatchlist(entity);
      default:
        throw new Error('Method not implemented.');
    }
  }

  mapExtendedSearchResult(
    search_result: ExhibitorSearchResult | BrandSearchResult,
    context: string | undefined = undefined,
    flat = false
  ): any {
    let object = undefined;

    switch (search_result.type) {
      case 'EXHIBITOR':
        object = {
          schema: CustomEventIglu.entity_exhibitor,
          data: {
            context: context,
            exhibitor_id: search_result.exhibitor.id,
            exhibitor_name: search_result.exhibitor.name,
            locale: this.locale,
          },
        };
        break;
      case 'BRAND':
        object = {
          schema: CustomEventIglu.entity_exhibitor,
          data: {
            context: context,
            exhibitor_id: search_result.brand.exhibitor?.id,
            exhibitor_name: search_result.brand.exhibitor?.name,
            locale: this.locale,
          },
        };
        break;
    }

    return flat ? object : [object];
  }

  mapSearchResult(search_result: SearchResult, context: string | undefined = undefined) {
    switch (search_result.type) {
      case 'EVENT':
        return [
          {
            schema: CustomEventIglu.entity_event,
            data: {
              context: context,
              locale: this.locale,
              event_id: search_result.id,
              event_name: search_result.name,
              starts_at: search_result.starts_at,
              ends_at: search_result.ends_at,
            },
          },
          {
            schema: CustomEventIglu.entity_exhibitor,
            data: {
              context: context,
              exhibitor_id: search_result.exhibitor_id,
              exhibitor_name: search_result.exhibitor_name,
              locale: this.locale,
            },
          },
        ];
      case 'EXHIBITOR':
        return [
          {
            schema: CustomEventIglu.entity_exhibitor,
            data: {
              context: context,
              exhibitor_id: search_result.id,
              exhibitor_name: search_result.name,
              locale: this.locale,
            },
          },
        ];
      case 'PRODUCT':
        return [
          {
            schema: CustomEventIglu.entity_product,
            data: {
              context: context,
              product_id: search_result.id,
              product_name: search_result.name,
              published_at: search_result.published_at,
              locale: this.locale,
            },
          },
          {
            schema: CustomEventIglu.entity_exhibitor,
            data: {
              context: context,
              exhibitor_id: search_result.exhibitor_id,
              exhibitor_name: search_result.exhibitor_name,
              locale: this.locale,
            },
          },
        ];
      case 'POST':
        return [
          {
            schema: CustomEventIglu.entity_post,
            data: {
              context: context,
              post_id: search_result.id,
              post_name: search_result.name,
              locale: this.locale,
              ...(search_result?.published_at && { published_at: search_result?.published_at }),
            },
          },
          {
            schema: CustomEventIglu.entity_exhibitor,
            data: {
              context: context,
              exhibitor_id: search_result.exhibitor_id,
              exhibitor_name: search_result.exhibitor_name,
              locale: this.locale,
            },
          },
        ];
      default:
        throw new Error('Method not implemented.');
    }
  }

  mapWatchlist(watchlist_change: WatchlistChangeInterface) {
    return [
      ...(watchlist_change.exhibitor
        ? [
            {
              schema: CustomEventIglu.entity_exhibitor,
              data: {
                context: watchlist_change.exhibitor.fair_id,
                locale: this.locale,
                exhibitor_id: watchlist_change.exhibitor.id,
                exhibitor_name: watchlist_change.exhibitor.name,
              },
            },
          ]
        : []),
      ...(watchlist_change.event
        ? [
            {
              schema: CustomEventIglu.entity_event,
              data: {
                context: watchlist_change.event.fair_id,
                locale: this.locale,
                event_id: watchlist_change.event.id,
                event_name: watchlist_change.event.headline,
              },
            },
          ]
        : []),
      ...(watchlist_change.post
        ? [
            {
              schema: CustomEventIglu.entity_post,
              data: {
                context: watchlist_change.post.fair_id,
                locale: this.locale,
                post_id: watchlist_change.post.id,
                post_name: watchlist_change.post.headline,
              },
            },
          ]
        : []),
      ...(watchlist_change.product
        ? [
            {
              schema: CustomEventIglu.entity_product,
              data: {
                context: watchlist_change.product.fair_id,
                locale: this.locale,
                product_id: watchlist_change.product.id,
                product_name: watchlist_change.product.name,
              },
            },
          ]
        : []),
      ...(watchlist_change.brand
        ? [
            {
              schema: CustomEventIglu.entity_exhibitor,
              data: {
                context: watchlist_change.brand.fair_id,
                locale: this.locale,
                exhibitor_id: watchlist_change.brand.id,
                exhibitor_name: watchlist_change.brand.name,
              },
            },
          ]
        : []),
    ];
  }

  filter(type: string, text?: string) {
    if (
      type === 'TELEPHONE_CONTACT_PERSON' ||
      type === 'MAIL_CONTACT_PERSON' ||
      type === 'TELEPHONE_COMPANY' ||
      type === 'MAIL_COMPANY'
    ) {
      return undefined;
    }

    return text;
  }
}
